import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useQuasar } from 'quasar'

export const useNotificationStore = defineStore('notification', () => {
  const $q = useQuasar()

  // Queue for notifications that need to be shown
  const notificationQueue = ref([])
  const isProcessing = ref(false)

  /**
   * Add a notification to the queue
   */
  const queueNotification = (notification) => {
    notificationQueue.value.push({
      id: Date.now() + Math.random(),
      ...notification
    })

    // Process queue if not already processing
    if (!isProcessing.value) {
      processQueue()
    }
  }

  /**
   * Process the notification queue
   */
  const processQueue = async () => {
    if (isProcessing.value || notificationQueue.value.length === 0) {
      return
    }

    isProcessing.value = true

    while (notificationQueue.value.length > 0) {
      const notification = notificationQueue.value.shift()

      // Wait for Quasar to be ready
      await waitForQuasar()

      // Show the notification
      $q.notify({
        type: notification.type || 'info',
        message: notification.message,
        position: notification.position || 'top',
        timeout: notification.timeout || 5000,
        icon: notification.icon,
        color: notification.color,
        textColor: notification.textColor,
        actions: notification.actions || []
      })

      // Small delay between notifications to prevent overlap
      await new Promise(resolve => setTimeout(resolve, 100))
    }

    isProcessing.value = false
  }

  /**
   * Wait for Quasar to be ready
   */
  const waitForQuasar = () => {
    return new Promise((resolve) => {
      if ($q && $q.notify) {
        resolve()
      } else {
        // Poll until Quasar is ready
        const checkQuasar = () => {
          if ($q && $q.notify) {
            resolve()
          } else {
            setTimeout(checkQuasar, 50)
          }
        }
        checkQuasar()
      }
    })
  }

  /**
   * Queue a notification that will be shown after a delay
   * Useful for router guards where Quasar might not be ready yet
   */
  const queueDelayedNotification = (notification, delay = 100) => {
    setTimeout(() => {
      queueNotification(notification)
    }, delay)
  }

  /**
   * Convenience methods for different notification types
   */
  const success = (message, options = {}) => {
    queueNotification({
      type: 'positive',
      message,
      icon: 'check_circle',
      ...options
    })
  }

  const error = (message, options = {}) => {
    queueNotification({
      type: 'negative',
      message,
      icon: 'error',
      ...options
    })
  }

  const warning = (message, options = {}) => {
    queueNotification({
      type: 'warning',
      message,
      icon: 'warning',
      ...options
    })
  }

  const info = (message, options = {}) => {
    queueNotification({
      type: 'info',
      message,
      icon: 'info',
      ...options
    })
  }

  // Delayed notification methods for router guards
  const delayedSuccess = (message, options = {}) => {
    queueDelayedNotification({
      type: 'positive',
      message,
      icon: 'check_circle',
      ...options
    })
  }

  const delayedError = (message, options = {}) => {
    queueDelayedNotification({
      type: 'negative',
      message,
      icon: 'error',
      ...options
    })
  }

  const delayedWarning = (message, options = {}) => {
    queueDelayedNotification({
      type: 'warning',
      message,
      icon: 'warning',
      ...options
    })
  }

  const delayedInfo = (message, options = {}) => {
    queueDelayedNotification({
      type: 'info',
      message,
      icon: 'info',
      ...options
    })
  }

  /**
   * Clear all pending notifications
   */
  const clearQueue = () => {
    notificationQueue.value = []
    isProcessing.value = false
  }

    return {
    // State
    notificationQueue,
    isProcessing,

    // Actions
    queueNotification,
    processQueue,
    clearQueue,
    queueDelayedNotification,

    // Convenience methods
    success,
    error,
    warning,
    info,

    // Delayed notification methods for router guards
    delayedSuccess,
    delayedError,
    delayedWarning,
    delayedInfo
  }
})
